import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  exchangeCodeForTokens,
  fetchUserInfo,
  parseCallback,
  type PendingAuth,
} from '@/lib/auth/auth0';
import { setAccessTokenOnService, storeLastEmail, storeRefreshToken } from '@/lib/auth/token-storage';
import { scheduleTokenRefresh } from '@/lib/auth/token-refresh-scheduler';
import { useAuthStore } from '@/stores/use-auth-store';
import { NulaLockup } from '@/components/nula-lockup/nula-lockup';
import { AppSplash } from '@/app/boot/app-splash';
import styles from './auth-callback.module.css';

/**
 * `/auth/callback` — web OAuth completion landing page.
 *
 * The browser tab navigated here from Auth0. Read the stashed PKCE state from
 * sessionStorage (put there by SignInCard), exchange the code for tokens, hydrate
 * the auth store, then redirect to /.
 *
 * Desktop sign-in does NOT use this route — it redirects to the dedicated
 * /auth/desktop bounce page (apps/lumen/src/app/routes/auth-desktop.tsx), which
 * deep-links back into the running Tauri app. (Desktop dev still lands here and
 * uses the same web exchange path below.)
 */
export function AuthCallback() {
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const search = window.location.search;
    if (!search || search === '?') {
      // Hit directly with no params — nothing to do. Send home (client-side nav,
      // not a hard reload, so the splash doesn't restart).
      void navigate({ to: '/', replace: true });
      return;
    }

    // Web OAuth completion: exchange the code in-page. (Desktop sign-in uses
    // the dedicated /auth/desktop bounce page, never this route.)
    let cancelled = false;
    void (async () => {
      const { code, state, error: oauthError } = parseCallback(window.location.href);
      if (oauthError) {
        setError(`Sign-in failed: ${oauthError}`);
        return;
      }
      if (!code || !state) {
        setError('Missing code or state in callback URL.');
        return;
      }

      const stashRaw = sessionStorage.getItem('auth0_pending');
      if (!stashRaw) {
        setError('No sign-in in progress. Please try again.');
        return;
      }
      const stash: PendingAuth = JSON.parse(stashRaw);
      sessionStorage.removeItem('auth0_pending');

      if (state !== stash.state) {
        setError('State mismatch — possible CSRF. Please try again.');
        return;
      }

      try {
        const redirectUri = `${window.location.origin}/auth/callback`;
        const tokens = await exchangeCodeForTokens(code, stash.codeVerifier, redirectUri);
        const userInfo = await fetchUserInfo(tokens.access_token);
        if (cancelled) return;

        await setAccessTokenOnService(tokens.access_token);
        scheduleTokenRefresh(tokens.expires_in);
        if (tokens.refresh_token) {
          await storeRefreshToken(tokens.refresh_token);
        }
        await storeLastEmail(userInfo.email);

        useAuthStore.getState().setFromAuth({
          user: {
            sub: userInfo.sub,
            email: userInfo.email,
            name: userInfo.name ?? '',
            picture: userInfo.picture ?? '',
          },
          accessToken: tokens.access_token,
          isAuthenticated: true,
          isLoading: false,
        });

        // Client-side navigation (NOT window.location.replace, which is a full
        // page reload that tears down the SPA and restarts the splash — the
        // "flash"). The auth store is already hydrated and configureAuth reads
        // the token lazily, so the app boots in-session: BootGate's splash takes
        // over continuously from this one.
        void navigate({ to: '/', replace: true });
      } catch (err) {
        console.error('[AuthCallback] token exchange failed:', err);
        setError(err instanceof Error ? err.message : 'Sign-in failed.');
      }
    })();

    return () => {
      cancelled = true;
    };
    // Run the token exchange exactly once on mount. navigate is stable (TanStack
    // Router) and intentionally excluded so a navigate-identity change can't
    // re-trigger the exchange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Error → keep the explicit card with a recovery action. Otherwise render the
  // same boot splash (logo + ambient orbs) as the app, so the token-exchange
  // step blends into the loading flow instead of reading as a separate page.
  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.card}>
          <NulaLockup height={36} className={styles.lockup} />
          <h1 className={styles.title}>Sign-in failed</h1>
          <p className={styles.message}>{error}</p>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => { window.location.href = '/login'; }}
          >
            Return to sign-in
          </button>
        </div>
      </div>
    );
  }

  // Render the SAME splash as the app (logo + orbs + subtitle) so the
  // token-exchange step is visually identical to the boot splash — no flash when
  // control hands off to BootGate's AppSplash after the redirect.
  // advanceBootState={false}: this page must not drive the boot state machine.
  // speed={10}: slow the splash 10× so the logo draws on gently and loops while
  // the token exchange runs — it never finishes-then-freezes, so the hand-off to
  // BootGate's splash doesn't look abrupt.
  return <AppSplash advanceBootState={false} speed={10} />;
}
